import { useEffect } from 'react';
import { Database, Beaker } from 'lucide-react';
import { useSampleLoader, type DbSample } from '@/lib/equipmentSpecUtils';

interface SampleSelectorProps {
  loader: ReturnType<typeof useSampleLoader>;
  onLoad?: (id: number) => void;
  showDilution?: boolean;
}

export function SampleSelector({ loader, onLoad, showDilution = true }: SampleSelectorProps) {
  const {
    dbSamples, loadingSamples, selectedSampleId, setSelectedSampleId,
    handleLoadSample, dilutionFactor, applyDilution, clearSample,
  } = loader;

  useEffect(() => {
    loader.loadDbSamples();
  }, []);

  return (
    <div className="p-4 space-y-3 rb-card">
      <div className="flex items-center gap-2">
        <Database className="w-4 h-4 text-sky-400" />
        <h3 className="text-sm font-bold text-white">Database Sample</h3>
      </div>

      {loadingSamples ? (
        <p className="text-xs text-slate-500">Loading samples...</p>
      ) : dbSamples.length === 0 ? (
        <p className="text-xs text-slate-500">No samples found in database.</p>
      ) : (
        <>
          <select
            value={selectedSampleId}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : '';
              setSelectedSampleId(id);
              if (id !== '') {
                handleLoadSample(id as number);
                onLoad?.(id as number);
              } else {
                clearSample();
              }
            }}
            className="w-full px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500 rb-inner-card-sm"
          >
            <option value="">— Select a recorded sample —</option>
            {dbSamples.map((s: DbSample) => (
              <option key={s.id} value={s.id}>
                {s.sample_name} {s.customer_name ? `(${s.customer_name})` : ''} — {s.created_at ? new Date(s.created_at).toLocaleDateString() : 'N/A'}
              </option>
            ))}
          </select>

          {showDilution && selectedSampleId !== '' && (
            <div className="flex items-center gap-3 pt-1">
              <Beaker className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs text-slate-400">Dilution:</span>
              <input
                type="range" min={1} max={10} step={0.5}
                value={dilutionFactor}
                onChange={(e) => applyDilution(parseFloat(e.target.value))}
                className="flex-1 h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: '#06b6d4' }}
              />
              <span className="text-xs font-bold text-cyan-400 w-10 text-right">{dilutionFactor.toFixed(1)}×</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
